import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Icons, LinkButton } from '@shopline/dashboard-ui';
import { useQueryInitialState } from 'hooks/useInitialState';
import ToastContext from 'contexts/Toast';
import { nanoid } from 'nanoid';
import { Well } from '../components/Well';
import Section from '../components/Section';
import usePermissions from 'hooks/usePermissions';
import CircleTickIcon from '../components/CircleTickIcon';
import { useQueryMerchant } from '../useMerchant';
import { PRODUCT_API_TYPE, PRODUCT_API_AUTH_TYPE } from '../constants';
import mobileImg from './mobile.png';
import {
  SectionRow,
  EnhancedWarningAlert as BaseEnhancedWarningAlert,
  FormContainer as BaseFormContainer,
  ImageContainer,
  Image,
  ActionBtns,
  TitleWithLogo,
  LoggedInContainer as BaseLoggedInContainer,
  EmailContainer,
  LogoutContainer,
  LogoutButton,
} from './StyledComponents';
import { useLogoutMerchantGoogleAccount } from '../useLogoutMerchantGoogleAccount';

const EnhandedSection = styled(Section)`
  margin-bottom: 16px;
`;

const EnhancedWarningAlert = styled(BaseEnhancedWarningAlert)`
  font-weight: 400;
  leading-trim: NONE;
  letter-spacing: 0px;

  h4 {
    font-size: 14px;
    line-height: 22px;
    margin: 0 0 4px 0;
  }

  p {
    margin: 0px;
    font-size: 12px;
    line-height: 18px;
  }
`;

const FormContainer = styled(BaseFormContainer)`
  ul {
    margin: 4px 0 14px 0;
    padding-left: 17px;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    letter-spacing: 0px;
  }
`;

const LoggedInContainer = styled(BaseLoggedInContainer)`
  ${Well} {
    margin-bottom: 16px;
  }

  ${EnhancedWarningAlert} {
    margin-bottom: 0;
  }
`;

const HelpText = styled.p`
  font-weight: 400;
  font-size: 14px;
  leading-trim: NONE;
  line-height: 22px;
  letter-spacing: 0px;
  color: ${({ theme }) => theme.colors.INK_600};
`;

const learnMoreUrl =
  'https://support.shoplineapp.com/hc/zh-tw/articles/23966890906649-Google-Merchant-Center-Content-API';

const OauthForm = () => {
  const { t } = useTranslation(['main', 'common']);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { isSuccess: isMerchantLoaded, data: merchant } = useQueryMerchant();
  const {
    refetch: refetchInitialState,
    isSuccess: isInitialStateLoaded,
    data: initialState,
  } = useQueryInitialState();
  const { createUIToast } = useContext(ToastContext);
  const {
    mutate: logoutMerchantGoogleAccount,
    isLoading: isLoggingOutMerchantGoogleAccount,
  } = useLogoutMerchantGoogleAccount({
    onSuccess: () => {
      refetchInitialState();
    },
    onError: (error) => {
      createUIToast({
        id: nanoid(),
        type: 'alert',
        titleWithParams: {
          key: 'common:Something went wrong. Please try again.',
        },
        duration: 5000,
      });
      console.error(error);
    },
  });
  const canEdit = usePermissions('merchant', 'edit');
  const hasIntegrated =
    isMerchantLoaded &&
    merchant?.productApiType === PRODUCT_API_TYPE.MERCHANT_API &&
    merchant?.productApiAuthType === PRODUCT_API_AUTH_TYPE.OAUTH_TOKEN;

  // 若已經串接至 Merchant API，則不顯示登出按鈕
  const showLogoutButton =
    isMerchantLoaded &&
    merchant?.productApiType !== PRODUCT_API_TYPE.MERCHANT_API;

  useEffect(() => {
    const handleMessage = (event) => {
      const { code } = event.data;
      if (code === 'login-success') {
        setIsAuthenticating(false);
        refetchInitialState();
        createUIToast({
          id: nanoid(),
          type: 'success',
          titleWithParams: {
            key: "main:You're signed in with Google",
          },
          duration: 5000,
        });
      } else if (code === 'login-error') {
        setIsAuthenticating(false);
        createUIToast({
          id: nanoid(),
          type: 'alert',
          titleWithParams: {
            key: 'common:Something went wrong. Please try again.',
          },
          duration: 5000,
        });
        console.error(event.data);
      }
    };
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [createUIToast, refetchInitialState]);

  const openGoogleAuth = () => {
    setIsAuthenticating(true);
    window.open('/auth/merchant-google-account', '_blank');
  };

  const merchantGoogleAccount =
    isInitialStateLoaded && initialState?.merchantGoogleAccount;

  return (
    <>
      {!merchantGoogleAccount && hasIntegrated && (
        <EnhancedWarningAlert>
          <h4>
            {t(
              'Please contact the Online Merchant Success Team to enable this feature for your store.',
            )}
          </h4>
          <p>
            {t(
              'To reconnect to Google Merchant Center, a new GMC account will be created following the new process to comply with Google\'s latest API standards. Please contact the Online Merchant Success Team to enable the "Automatically create and connect Merchant API" feature.',
            )}
          </p>
        </EnhancedWarningAlert>
      )}
      <EnhandedSection>
        {!merchantGoogleAccount && (
          <SectionRow>
            <FormContainer>
              <TitleWithLogo>
                <div className="logo">
                  <Icons.Brand.ShoplineDefault size="MEDIUM" />
                  <Icons.Brand.Google size="MEDIUM" />
                </div>
                SHOPLINE X Google Merchant Center
              </TitleWithLogo>
              <h4>
                {t(
                  'Sign in and authorize now to upgrade from Content API to Merchant API',
                )}
              </h4>
              <ul>
                <li>
                  {t(
                    'Due to Google policy changes, the Content API will be deprecated on 18 August 2026, and replaced by the Merchant API.',
                  )}
                </li>
                <li>
                  {t(
                    'Please complete the API upgrade as soon as possible to prevent any disruption to product uploads and ad delivery.',
                  )}
                </li>
                <li>
                  {t(
                    'Once authorized, you can upgrade to the Merchant API instantly with no manual setup required.',
                  )}
                </li>
              </ul>
              <EnhancedWarningAlert>
                {t(
                  "We recommend that the store owner perform the authorization to avoid disruptions caused by changes to a staff member's account status or permissions.",
                )}
              </EnhancedWarningAlert>
              <ActionBtns>
                <Button.Primary
                  type="button"
                  onClick={openGoogleAuth}
                  isLoading={isAuthenticating}
                  disabled={!canEdit || hasIntegrated}
                >
                  {t('Sign in with Google')}
                </Button.Primary>
                <LinkButton.Default
                  type="button"
                  href={learnMoreUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t('Learn more')}
                </LinkButton.Default>
              </ActionBtns>
            </FormContainer>
            <ImageContainer>
              <Image src={mobileImg} />
            </ImageContainer>
          </SectionRow>
        )}
        {merchantGoogleAccount && (
          <LoggedInContainer>
            <TitleWithLogo>
              <div className="logo">
                <Icons.Brand.ShoplineDefault size="MEDIUM" />
                <Icons.Brand.Google size="MEDIUM" />
              </div>
              SHOPLINE X Google Merchant Center
            </TitleWithLogo>
            <p>{t('Signed in with Google')}</p>
            <Well>
              <EmailContainer>
                <CircleTickIcon size="MEDIUM" />
                {merchantGoogleAccount.email}
              </EmailContainer>
              {showLogoutButton && (
                <LogoutContainer>
                  <LogoutButton
                    onClick={() =>
                      logoutMerchantGoogleAccount(initialState?.merchant?.id)
                    }
                    disabled={!canEdit || isLoggingOutMerchantGoogleAccount}
                  >
                    {t('Logout', { ns: 'common' })}
                  </LogoutButton>
                </LogoutContainer>
              )}
            </Well>
            <HelpText>
              {t(
                "If the authorized staff member's GMC account is suspended or permissions change, product uploads and ad delivery will be affected. If any changes occur, please contact the Online Merchant Success Team.",
              )}
            </HelpText>
          </LoggedInContainer>
        )}
      </EnhandedSection>
    </>
  );
};

export default OauthForm;
